import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithPopover from "@/components/form/labels/LabelWithPopover";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import { InputNumber } from "@/components/InputNumber";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency, formatLocaleNumber } from "@/utils/formatUtils";
import {
  licensePlateNormalizeFunction,
  resolveFormValidationError,
  upperCaseStringNormalizeFunction,
} from "@/utils/formUtils";
import { tExists } from "@/utils/translationUtils";
import { isDefined, removeStringWhiteSpaces, toDate } from "@/utils/utils";
import { validationFunctions, validations } from "@/utils/validationUtils";
import {
  App as AntApp,
  AutoComplete,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  Row,
  Select,
  Spin,
} from "antd";
import { DataSourceItemObject } from "antd/es/auto-complete/AutoComplete";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { Rule } from "antd/lib/form";
import classNames from "classnames";
import dayjs from "dayjs";
import debounce from "lodash/debounce";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import t from "../../../../../../../app/i18n";
import {
  useDownloadCardReader,
  useGetVehiclePrice,
  useGetVehiclesAutocomplete,
} from "../../../../../../../common/api/queries";
import { rowGutter } from "../../../../../../../common/constants";
import { Permission } from "../../../../../../../common/security/enums";
import ComponentWithPermission from "../../../../../../auth/components/ComponentWithPermission";
import { useIsCurrentAgentTypePlus } from "../../../../../../auth/hooks";
import {
  Bodywork,
  FuelType,
  Transmission,
  TransmissionGearCount,
  VehicleCategory,
} from "../../../../../../contract/enums";
import { Vehicle } from "../../../../../../contract/types";
import { VehicleBrandSelect } from "../../../../../../enumerations/components/form/VehicleBrandSelect";
import { VehicleModelSelect } from "../../../../../../enumerations/components/form/VehicleModelSelect";
import { showVehicleFoundModal } from "../../../../../../vehicle/autocomplete/components/VehicleFoundModal";
import { AutocompleteRequestOrigin, AutocompleteVehiclesBy } from "../../../../../../vehicle/autocomplete/enums";
import { CalcType } from "../../../../../enums";
import { VehicleImportCountry, VehiclePurpose, VehicleSecurityFeature, VehicleServiceType } from "../../../enums";
import { VehicleCalcForm } from "../../../types";
import {
  CALC_OMITTED_CATEGORIES,
  ELECTRIC_ENGINE_POWER_FUEL_TYPES,
  EXCLUDE_VEHICLE_CATEGORIES_SFA_PLUS,
  isElectroPackageFuelType,
  REINSURANCES_MAP,
  resolveOdometerPerYearValues,
  SPECS_MAP,
  VEHICLE_CATEGORY_L,
  VEHICLE_CATEGORY_M1_N1,
} from "../../../utils";

const colSpan = 4;
const threeColumnColSpan = 8;
const AUTOCOMPLETE_MAX_SIZE = 7;

const licensePlateLengthRule: Rule = {
  validator: (_, value) => {
    const data = removeStringWhiteSpaces(value);
    return data && data.length !== 6 ? Promise.reject(t("validation.length", { len: 6 })) : Promise.resolve();
  },
};

export const VehicleCalcVehicleDataSection = () => {
  const { t } = useTranslation();
  const { notification, modal } = AntApp.useApp();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const getVehiclePrice = useGetVehiclePrice();
  const downloadCardReader = useDownloadCardReader();

  const [autocompleteVehicleBy, setAutocompleteVehicleBy] = useState<AutocompleteVehiclesBy>(
    AutocompleteVehiclesBy.LICENSE_PLATE,
  );
  const [autocompleteKeyword, setAutocompleteKeyword] = useState<string>();

  const [isOtherBrandChosen, setIsOtherBrandChosen] = useState<boolean>();
  const [isOtherModelChosen, setIsOtherModelChosen] = useState<boolean>();

  const { data: vehicleAutocompleteResult, isFetching } = useGetVehiclesAutocomplete(
    {
      keyword: removeStringWhiteSpaces(autocompleteKeyword?.toUpperCase()),
      by: autocompleteVehicleBy,
      maxResultSize: AUTOCOMPLETE_MAX_SIZE,
      origin: AutocompleteRequestOrigin.CALC,
    },
    isTopAgentPlus,
  );

  const form = Form.useFormInstance<VehicleCalcForm>();
  const category = Form.useWatch(["vehicleData", "category"], form);
  const purpose = Form.useWatch(["vehicleData", "purpose"], form);
  const newVehicle = Form.useWatch(["vehicleData", "newVehicle"], form);
  const brandId = Form.useWatch(["vehicleData", "brandId"], form);
  const customBrand = Form.useWatch(["vehicleData", "customBrand"], { form, preserve: true });
  const customModel = Form.useWatch(["vehicleData", "customModel"], { form, preserve: true });
  const fuelType = Form.useWatch(["vehicleData", "fuelType"], form);
  const calcType = Form.useWatch("type", form);
  const importedChecked = Form.useWatch(["vehicleData", "imported"], form);
  const price = Form.useWatch(["vehicleData", "price"], form);

  const extendedAssistance = Form.useWatch(["reinsurancesData", "extendedAssistance"], form);
  const injury = Form.useWatch(["reinsurancesData", "injury"], form);
  const element = Form.useWatch(["reinsurancesData", "element"], form);

  const isMotorcycleWithReinsurance =
    VEHICLE_CATEGORY_L.includes(category) && (extendedAssistance || injury || element);

  const odometerPerYearDescriptions =
    calcType === CalcType.MTPL ? [1, 2] : calcType === CalcType.CRASH ? [1, 3] : [1, 2, 3];

  const odometerPerYearOptions = useMemo(() => {
    const values = resolveOdometerPerYearValues(category);
    return values.map((value, index) => ({
      value,
      label:
        index === 0
          ? t("calc.vehicle.helpers.odometerPerYearMin", { value: formatLocaleNumber(value) })
          : index === values.length - 1
            ? t("calc.vehicle.helpers.odometerPerYearMax", { value: formatLocaleNumber(value) })
            : t("calc.vehicle.helpers.odometerPerYearValue", { value: formatLocaleNumber(value) }),
    }));
  }, [category, t]);

  // Until the user picks a brand or model, a custom value already in the form reveals its field
  const showCustomBrandInput = isOtherBrandChosen ?? !!customBrand;
  const showCustomModelInput = isOtherModelChosen ?? !!customModel;

  const handleNewVehicleChange = (event: CheckboxChangeEvent) => {
    const { type, vehicleData } = form.getFieldsValue([["type"], ["vehicleData"]]) as VehicleCalcForm;
    form.setFieldsValue({
      vehicleData: {
        firstRegistrationDate: undefined,
        price: type !== CalcType.MTPL || event.target.checked ? vehicleData.price : undefined,
        registrationCertificatePresent: !event.target.checked,
      },
    });
  };

  const handleBrandIdChange = () => {
    form.setFieldsValue({ vehicleData: { modelId: undefined } });
  };

  const handleCategoryChange = (category: VehicleCategory) => {
    const { type, vehicleData, reinsurancesData } = form.getFieldsValue([
      ["type"],
      ["vehicleData"],
      ["reinsurancesData"],
    ]) as VehicleCalcForm;

    form.setFieldsValue({
      vehicleData: {
        fuelType: SPECS_MAP.get("fuelType")?.includes(category) ? vehicleData.fuelType : undefined,
        transmission: SPECS_MAP.get("transmission")?.includes(category) ? vehicleData.transmission : undefined,
        transmissionGearCount: SPECS_MAP.get("transmissionGearCount")?.includes(category)
          ? vehicleData.transmissionGearCount
          : undefined,
        bodywork: SPECS_MAP.get("bodywork")?.includes(category) ? vehicleData.bodywork : undefined,
        fourWheelDrive: SPECS_MAP.get("fourWheelDrive")?.includes(category) ? vehicleData.fourWheelDrive : false,
        seatsNumber: SPECS_MAP.get("seatsNumber")?.includes(category) ? vehicleData.seatsNumber : undefined,
        doorsNumber: SPECS_MAP.get("doorsNumber")?.includes(category) ? vehicleData.doorsNumber : undefined,
        engineDisplacement: SPECS_MAP.get("engineDisplacement")?.includes(category)
          ? vehicleData.engineDisplacement
          : undefined,
        enginePower: SPECS_MAP.get("enginePower")?.includes(category) ? vehicleData.enginePower : undefined,
        electricEnginePower: SPECS_MAP.get("electricEnginePower")?.includes(category)
          ? vehicleData.electricEnginePower
          : undefined,
        odometer: SPECS_MAP.get("odometer")?.includes(category) ? vehicleData.odometer : undefined,
        odometerPerYear:
          isDefined(vehicleData.odometerPerYear) &&
          resolveOdometerPerYearValues(category).includes(vehicleData.odometerPerYear)
            ? vehicleData.odometerPerYear
            : undefined,
        steeringWheelOnTheRight: SPECS_MAP.get("steeringWheelOnTheRight")?.includes(category)
          ? vehicleData.steeringWheelOnTheRight
          : false,
        emergencyBrakingSystem: SPECS_MAP.get("emergencyBrakingSystem")?.includes(category)
          ? vehicleData.emergencyBrakingSystem
          : false,
        parkingAssistant: SPECS_MAP.get("parkingAssistant")?.includes(category) ? vehicleData.parkingAssistant : false,
        price: type === CalcType.MTPL && !VEHICLE_CATEGORY_M1_N1.includes(category) ? undefined : vehicleData.price,
      },
      reinsurancesData: {
        extendedAssistance: REINSURANCES_MAP.get("extendedAssistance")?.includes(category)
          ? reinsurancesData.extendedAssistance
          : false,
        glass: REINSURANCES_MAP.get("glass")?.includes(category) ? reinsurancesData.glass : false,
        animal: REINSURANCES_MAP.get("animal")?.includes(category) ? reinsurancesData.animal : false,
        element: REINSURANCES_MAP.get("element")?.includes(category) ? reinsurancesData.element : false,
        elementAmount: REINSURANCES_MAP.get("element")?.includes(category) ? reinsurancesData.elementAmount : undefined,
        theft: REINSURANCES_MAP.get("theft")?.includes(category) ? reinsurancesData.theft : false,
        vandalism: REINSURANCES_MAP.get("vandalism")?.includes(category) ? reinsurancesData.vandalism : false,
        injury: REINSURANCES_MAP.get("injury")?.includes(category) ? reinsurancesData.injury : false,
        injuryAmount: REINSURANCES_MAP.get("injury")?.includes(category) ? reinsurancesData.injuryAmount : undefined,
        gap: REINSURANCES_MAP.get("gap")?.includes(category) ? reinsurancesData.gap : false,
        replacementVehicle: REINSURANCES_MAP.get("replacementVehicle")?.includes(category)
          ? reinsurancesData.replacementVehicle
          : false,
        generaliAbroadVehicleRepair: REINSURANCES_MAP.get("generaliAbroadVehicleRepair")?.includes(category)
          ? reinsurancesData.generaliAbroadVehicleRepair
          : false,
        generaliElectroPackage:
          REINSURANCES_MAP.get("generaliElectroPackage")?.includes(category) &&
          isElectroPackageFuelType(vehicleData.fuelType)
            ? reinsurancesData.generaliElectroPackage
            : false,
        koopExtendedWarranty: REINSURANCES_MAP.get("koopExtendedWarranty")?.includes(category)
          ? reinsurancesData.koopExtendedWarranty
          : false,
        uniqaSmallDamage: REINSURANCES_MAP.get("uniqaSmallDamage")?.includes(category)
          ? reinsurancesData.uniqaSmallDamage
          : false,
        uniqaTotalDamage: REINSURANCES_MAP.get("uniqaTotalDamage")?.includes(category)
          ? reinsurancesData.uniqaTotalDamage
          : false,
        uniqaLawyerAssistance: REINSURANCES_MAP.get("uniqaLawyerAssistance")?.includes(category)
          ? reinsurancesData.uniqaLawyerAssistance
          : false,
        uniqaSidecarMtpl: REINSURANCES_MAP.get("uniqaSidecarMtpl")?.includes(category)
          ? reinsurancesData.uniqaSidecarMtpl
          : false,
        uniqaChildrenInjury: REINSURANCES_MAP.get("uniqaChildrenInjury")?.includes(category)
          ? reinsurancesData.uniqaChildrenInjury
          : false,
      },
    });
  };

  const handleFuelTypeChange = (fuelType: FuelType) => {
    const { vehicleData, reinsurancesData } = form.getFieldsValue([
      ["vehicleData"],
      ["reinsurancesData"],
    ]) as VehicleCalcForm;
    form.setFieldsValue({
      vehicleData: {
        engineDisplacement: fuelType !== FuelType.BEV ? vehicleData.engineDisplacement : undefined,
        enginePower: fuelType !== FuelType.BEV ? vehicleData.enginePower : undefined,
        electricEnginePower: ELECTRIC_ENGINE_POWER_FUEL_TYPES.includes(fuelType)
          ? vehicleData.electricEnginePower
          : undefined,
      },
      reinsurancesData: {
        generaliElectroPackage: isElectroPackageFuelType(fuelType) ? reinsurancesData.generaliElectroPackage : false,
      },
    });
  };

  const handleVehiclePriceRequestSubmit = () => {
    form
      .validateFields([
        ["vehicleData", "vin"],
        ["vehicleData", "category"],
        ["vehicleData", "firstRegistrationDate"],
        ["vehicleData", "engineDisplacement"],
        ["vehicleData", "enginePower"],
        ["vehicleData", "electricEnginePower"],
        ["vehicleData", "totalWeight"],
        ["vehicleData", "fuelType"],
        ["vehicleData", "transmission"],
        ["vehicleData", "brandId"],
        ["vehicleData", "modelId"],
        ["vehicleData", "customBrand"],
        ["vehicleData", "customModel"],
      ])
      .then(async (values) => {
        if (!values.vehicleData.vin) {
          notification.error({
            title: t("common.error"),
            description: t("calc.vehicle.validations.vehiclePriceRequestError"),
            duration: 10,
          });
        } else {
          const vehiclePrice = await getVehiclePrice.mutateAsync({
            vin: values.vehicleData.vin,
            firstRegistrationDate: values.vehicleData.firstRegistrationDate,
            brandId: values.vehicleData.brandId,
            modelId: values.vehicleData.modelId,
            customBrand: values.vehicleData.customBrand,
            customModel: values.vehicleData.customModel,
            category: values.vehicleData.category,
            engineDisplacement: values.vehicleData.engineDisplacement,
            enginePower: values.vehicleData.enginePower,
            electricEnginePower: values.vehicleData.electricEnginePower,
            fuelType: values.vehicleData.fuelType,
            transmission: values.vehicleData.transmission,
            totalWeight: values.vehicleData.totalWeight,
          });

          if (vehiclePrice === 0) {
            notification.warning({
              title: t("common.warning"),
              description: t("calc.vehicle.helpers.vehiclePriceNoResponseDesc"),
            });
            form.setFieldsValue({ vehicleData: { price: undefined } });
          } else {
            notification.info({
              title: t("calc.vehicle.helpers.vehiclePriceResponseTitle"),
              description: t("calc.vehicle.helpers.vehiclePriceResponseDesc", {
                price: formatLocaleCurrency(vehiclePrice, 0),
              }),
            });

            form.setFieldsValue({ vehicleData: { price: vehiclePrice } });
          }
        }
      })
      .catch(resolveFormValidationError);
  };

  const handleVehicleAutocompleteSearchDebounced = useMemo(
    () =>
      debounce((value: string, focusedItem: AutocompleteVehiclesBy) => {
        const keyword = removeStringWhiteSpaces(value);

        if (validationFunctions.validateSearchKeyword(keyword)) {
          setAutocompleteKeyword(keyword);
          setAutocompleteVehicleBy(focusedItem);
        }
      }, 500),
    [],
  );

  const handleVehicleAutocompleteSearch = useCallback(
    (value: string, focusedItem: AutocompleteVehiclesBy) => {
      handleVehicleAutocompleteSearchDebounced(value, focusedItem);
    },
    [handleVehicleAutocompleteSearchDebounced],
  );

  const handleVehicleAutocompleteSelect = (value: string, focusedItem: AutocompleteVehiclesBy) => {
    setVehicleDataToForm(
      vehicleAutocompleteResult?.data.find((vehicle) =>
        focusedItem === AutocompleteVehiclesBy.VIN
          ? vehicle.vin === value
          : vehicle.licensePlate === removeStringWhiteSpaces(value),
      ) as Vehicle,
      focusedItem,
    );
  };

  const resolveVehicleAutocompleteOptions = (focusedItem: AutocompleteVehiclesBy): DataSourceItemObject[] => {
    return focusedItem === vehicleAutocompleteResult?.by
      ? vehicleAutocompleteResult?.data.map<DataSourceItemObject>((vehicle) => ({
          value:
            focusedItem === AutocompleteVehiclesBy.VIN
              ? vehicle.vin
              : (licensePlateNormalizeFunction(vehicle.licensePlate) ?? ""),
          text:
            focusedItem === AutocompleteVehiclesBy.VIN
              ? vehicle.vin
              : (licensePlateNormalizeFunction(vehicle.licensePlate) ?? ""),
        }))
      : [];
  };

  const setVehicleDataToForm = (vehicle: Vehicle, focusedItem?: AutocompleteVehiclesBy) => {
    const { vehicleData } = form.getFieldsValue([["vehicleData"]]) as VehicleCalcForm;

    const brandId = vehicle.model?.brand?.id || undefined;
    const modelId = brandId && vehicle.model ? vehicle.model.id : undefined;
    const firstRegistrationDate = vehicle.firstRegistrationDate;
    const newVehicle = !!firstRegistrationDate && toDate(firstRegistrationDate)?.isSameOrAfter(dayjs(), "day");
    const licensePlate =
      focusedItem === AutocompleteVehiclesBy.VIN && vehicleData.licensePlate
        ? vehicleData.licensePlate
        : licensePlateNormalizeFunction(vehicle.licensePlate);
    const isDifferentCategory = !!vehicleData.category && vehicle.category !== vehicleData.category;

    showVehicleFoundModal({
      modal,
      vehicle,
      licensePlate: vehicleData.licensePlate,
      focusedItem,
      onConfirm: () => {
        form.setFieldsValue({
          vehicleData: {
            licensePlate,
            vin: vehicle.vin,
            newVehicle,
            firstRegistrationDate,
            brandId,
            modelId,
            customBrand: vehicle.customBrand,
            customModel: vehicle.customModel,
            category: CALC_OMITTED_CATEGORIES.includes(vehicle.category) ? undefined : vehicle.category,
            engineDisplacement: vehicle.engineDisplacement,
            enginePower: vehicle.enginePower,
            electricEnginePower: vehicle.electricEnginePower,
            fuelType: vehicle.fuelType,
            transmission: vehicle.transmission,
            bodywork: vehicle.bodywork,
            purpose: undefined,
            totalWeight: vehicle.totalWeight,
            seatsNumber: vehicle.seatsNumber,
            doorsNumber: vehicle.doorsNumber,
            fourWheelDrive: vehicle.fourWheelDrive,
            odometer: undefined,
            odometerPerYear: undefined,
            garaged: false,
            imported: false,
            importCountry: undefined,
            steeringWheelOnTheRight: false,
            emergencyBrakingSystem: false,
            parkingAssistant: false,
            price: undefined,
            generalPrice: undefined,
            buyingPrice: undefined,
            security: vehicleData.security.map((value) => ({
              ...value,
              selected: false,
            })),
            registrationCertificatePresent: !newVehicle,
            registrationCertificateNumber: undefined,
            previousRegistrationCertificateNumber: vehicle.registrationCertificateNumber,
            colorId: vehicle.color?.id,
          },
        });

        // A found vehicle with a custom value reveals its field, one without leaves the field as it is shown now
        setIsOtherBrandChosen(!!vehicle.customBrand || showCustomBrandInput);
        setIsOtherModelChosen(!!vehicle.customModel || showCustomModelInput);

        if (isDifferentCategory) {
          handleCategoryChange(vehicle.category);
        }
      },
    });
  };

  useEffect(() => {
    const vehicle = vehicleAutocompleteResult?.data[0];
    const vehicleAutocompleteBy = vehicleAutocompleteResult?.by;

    const isExactSingleMatch =
      vehicleAutocompleteResult?.data.length === 1 &&
      vehicleAutocompleteResult?.keyword ===
        (vehicleAutocompleteBy === AutocompleteVehiclesBy.VIN ? vehicle?.vin : vehicle?.licensePlate);

    if (!isExactSingleMatch || !vehicle) {
      return;
    }

    setVehicleDataToForm(vehicle, vehicleAutocompleteBy);
  }, [vehicleAutocompleteResult]);

  return (
    <Card
      type="inner"
      className="card-box card-box--inner-extra"
      title={t("calc.vehicle.sections.vehicleData")}
      extra={
        <ComponentWithPermission permissions={[Permission.CARD_READER]}>
          <SecondaryButton size="small" icon={<AntIcon type="download" />} onClick={() => downloadCardReader.mutate()}>
            {t("calc.helpers.downloadCardReader")}
          </SecondaryButton>
        </ComponentWithPermission>
      }
    >
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Spin spinning={isFetching && autocompleteVehicleBy === AutocompleteVehiclesBy.LICENSE_PLATE}>
            <Form.Item
              name={["vehicleData", "licensePlate"]}
              label={
                <LabelWithTooltip
                  label={t("calc.vehicle.attrs.vehicleData.licensePlate")}
                  tooltip={t("calc.vehicle.helpers.licensePlateDesc")}
                />
              }
              dependencies={[["vehicleData", "purpose"]]}
              rules={
                !purpose
                  ? [validations.licensePlate]
                  : purpose === VehiclePurpose.VETERAN_CAR_H
                    ? [validations.licensePlate, licensePlateLengthRule]
                    : [validations.fullLicensePlate]
              }
              normalize={licensePlateNormalizeFunction}
            >
              <AutoComplete
                popupMatchSelectWidth={false}
                showSearch={{
                  onSearch: (value) => handleVehicleAutocompleteSearch(value, AutocompleteVehiclesBy.LICENSE_PLATE),
                }}
                onSelect={(value: string) =>
                  handleVehicleAutocompleteSelect(value, AutocompleteVehiclesBy.LICENSE_PLATE)
                }
                options={resolveVehicleAutocompleteOptions(AutocompleteVehiclesBy.LICENSE_PLATE)}
              />
            </Form.Item>
          </Spin>
        </Col>

        <Col span={colSpan}>
          <Spin spinning={isFetching && autocompleteVehicleBy === AutocompleteVehiclesBy.VIN}>
            <Form.Item
              name={["vehicleData", "vin"]}
              label={t("calc.vehicle.attrs.vehicleData.vin")}
              rules={[newVehicle ? validations.none : validations.notBlank, validations.size(3, 17)]}
              normalize={upperCaseStringNormalizeFunction}
            >
              <AutoComplete
                popupMatchSelectWidth={false}
                showSearch={{ onSearch: (value) => handleVehicleAutocompleteSearch(value, AutocompleteVehiclesBy.VIN) }}
                onSelect={(value: string) => handleVehicleAutocompleteSelect(value, AutocompleteVehiclesBy.VIN)}
                options={resolveVehicleAutocompleteOptions(AutocompleteVehiclesBy.VIN)}
              />
            </Form.Item>
          </Spin>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "newVehicle"]}
            valuePropName="checked"
            className="form-item-without-label"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox onChange={handleNewVehicleChange}>{t("calc.vehicle.attrs.vehicleData.newVehicle")}</Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "firstRegistrationDate"]}
            label={t("calc.vehicle.attrs.vehicleData.firstRegistrationDate")}
            rules={[validations.notNull, newVehicle ? validations.notPast : validations.notPresentAndFuture]}
            {...datePickerFormItemProps}
          >
            <DatePicker
              {...datePickerStandardProps}
              minDate={newVehicle ? dayjs() : undefined}
              maxDate={newVehicle ? undefined : dayjs().subtract(1, "day")}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <VehicleBrandSelect
            formItemProps={{
              name: ["vehicleData", "brandId"],
              label: (
                <LabelWithTooltip
                  label={t("calc.vehicle.attrs.vehicleData.brandId")}
                  tooltip={t("calc.vehicle.helpers.vehicleBrandDesc")}
                />
              ),
              rules: [validations.notNull],
            }}
            selectProps={{
              onChange: handleBrandIdChange,
            }}
            onChangeIsOther={setIsOtherBrandChosen}
          />
        </Col>

        <Col span={colSpan}>
          <VehicleModelSelect
            formItemProps={{
              name: ["vehicleData", "modelId"],
              label: (
                <LabelWithTooltip
                  label={t("calc.vehicle.attrs.vehicleData.modelId")}
                  tooltip={t("calc.vehicle.helpers.vehicleModelDesc")}
                />
              ),
              rules: [validations.notNull],
            }}
            brandId={brandId}
            selectProps={{
              placeholder: t("calc.vehicle.helpers.modelSelectPlaceholder"),
            }}
            onChangeIsOther={setIsOtherModelChosen}
          />
        </Col>
      </Row>

      {(showCustomBrandInput || showCustomModelInput) && (
        <Row gutter={rowGutter}>
          {showCustomBrandInput && (
            <Col span={colSpan} offset={colSpan * 4}>
              <Form.Item
                name={["vehicleData", "customBrand"]}
                label={t("calc.vehicle.attrs.vehicleData.customBrand")}
                rules={[validations.notNull, validations.size(1, 255)]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
          {showCustomModelInput && (
            <Col span={colSpan} offset={showCustomBrandInput ? 0 : colSpan * 5}>
              <Form.Item
                name={["vehicleData", "customModel"]}
                label={t("calc.vehicle.attrs.vehicleData.customModel")}
                rules={[validations.notNull, validations.size(1, 255)]}
              >
                <Input />
              </Form.Item>
            </Col>
          )}
        </Row>
      )}

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "category"]}
            label={t("contract.enums.vehicleCategory._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.values(VehicleCategory)
                .filter((category) => !CALC_OMITTED_CATEGORIES.includes(category))
                .filter((category) => !isTopAgentPlus || !EXCLUDE_VEHICLE_CATEGORIES_SFA_PLUS.includes(category))
                .map((category) => ({ value: category, label: t("contract.enums.vehicleCategory." + category) }))}
              onChange={handleCategoryChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "fuelType"]}
            label={t("contract.enums.fuelType._label")}
            rules={[validations.none]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              disabled={!SPECS_MAP.get("fuelType")?.includes(category)}
              options={Object.keys(FuelType).map((type) => ({
                value: type,
                label: t("contract.enums.fuelType." + type),
              }))}
              onChange={handleFuelTypeChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "engineDisplacement"]}
            label={t("calc.vehicle.attrs.vehicleData.engineDisplacement")}
            rules={[validations.minNumber(1)]}
          >
            <InputNumber
              addon="engineDisplacement"
              disabled={!SPECS_MAP.get("engineDisplacement")?.includes(category) || fuelType === FuelType.BEV}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "enginePower"]}
            label={t("calc.vehicle.attrs.vehicleData.enginePower")}
            rules={[validations.minNumber(1)]}
          >
            <InputNumber
              addon="enginePower"
              disabled={!SPECS_MAP.get("enginePower")?.includes(category) || fuelType === FuelType.BEV}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "electricEnginePower"]}
            label={
              <LabelWithTooltip
                label={t("calc.vehicle.attrs.vehicleData.electricEnginePower")}
                tooltip={t("calc.vehicle.helpers.electricEnginePowerDesc")}
              />
            }
            rules={[validations.minNumber(1)]}
          >
            <InputNumber
              addon="enginePower"
              disabled={
                !SPECS_MAP.get("electricEnginePower")?.includes(category) ||
                !fuelType ||
                !ELECTRIC_ENGINE_POWER_FUEL_TYPES.includes(fuelType)
              }
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "transmission"]}
            label={t("contract.enums.transmission._label")}
            rules={[validations.none]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              disabled={!SPECS_MAP.get("transmission")?.includes(category)}
              options={Object.keys(Transmission).map((transmission) => ({
                value: transmission,
                label: t("contract.enums.transmission." + transmission),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "transmissionGearCount"]}
            label={t("contract.enums.transmissionGearCount._label")}
            rules={[validations.none]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              disabled={!SPECS_MAP.get("transmissionGearCount")?.includes(category)}
              options={Object.keys(TransmissionGearCount).map((gearCount) => ({
                value: gearCount,
                label: t("contract.enums.transmissionGearCount." + gearCount),
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "bodywork"]}
            label={t("calc.vehicle.attrs.vehicleData.bodywork")}
            rules={[validations.none]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              disabled={!SPECS_MAP.get("bodywork")?.includes(category)}
              options={Object.keys(Bodywork).map((bodywork) => ({
                value: bodywork,
                label: t("contract.enums.bodywork." + bodywork),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "purpose"]}
            label={t("calc.vehicle.enums.vehiclePurpose._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.keys(VehiclePurpose).map((purpose) => ({
                value: purpose,
                label: t("calc.vehicle.enums.vehiclePurpose." + purpose),
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "totalWeight"]}
            label={t("calc.vehicle.attrs.vehicleData.totalWeight")}
            rules={[validations.notNull, validations.minNumber(1)]}
          >
            <InputNumber addon="kg" />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "seatsNumber"]}
            label={t("calc.vehicle.attrs.vehicleData.seatsNumber")}
            rules={[validations.minNumber(0)]}
          >
            <InputNumber disabled={!SPECS_MAP.get("seatsNumber")?.includes(category)} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "doorsNumber"]}
            label={t("calc.vehicle.attrs.vehicleData.doorsNumber")}
            rules={[validations.minNumber(0)]}
          >
            <InputNumber disabled={!SPECS_MAP.get("doorsNumber")?.includes(category)} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "fourWheelDrive"]}
            valuePropName="checked"
            className="form-item-without-label"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox disabled={!SPECS_MAP.get("fourWheelDrive")?.includes(category)}>
              {t("calc.vehicle.attrs.vehicleData.fourWheelDrive")}
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "serviceType"]}
            label={
              <LabelWithPopover
                label={t("calc.vehicle.enums.serviceType._label")}
                className="margin-left-tiny"
                popoverContent={
                  <table className="data-table-view wide-margins" style={{ maxWidth: 650 }}>
                    <tbody>
                      {[1, 2].map((value) => (
                        <tr key={value}>
                          <td>
                            <b>{t("calc.vehicle.helpers.serviceTypeDesc.institution" + value)}</b>
                          </td>
                          <td>
                            <ul>
                              {tExists(`calc.vehicle.helpers.serviceTypeDesc.contractDesc${value}`) && (
                                <li>
                                  <Trans
                                    i18nKey={`calc.vehicle.helpers.serviceTypeDesc.contractDesc${value}`}
                                    components={{ a: <a target="_blank" rel="noreferrer" /> }}
                                  />
                                </li>
                              )}
                              {tExists(`calc.vehicle.helpers.serviceTypeDesc.arbitraryDesc${value}`) && (
                                <li>
                                  <Trans i18nKey={`calc.vehicle.helpers.serviceTypeDesc.arbitraryDesc${value}`} />
                                </li>
                              )}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              />
            }
            rules={[validations.none]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              options={Object.values(VehicleServiceType).map((item) => ({
                label: t(`calc.vehicle.enums.serviceType.${item}`),
                value: item,
              }))}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "odometer"]}
            label={t("calc.vehicle.attrs.vehicleData.odometer")}
            rules={[validations.minNumber(0)]}
          >
            <InputNumber
              addon="distance"
              disabled={calcType === CalcType.MTPL || !SPECS_MAP.get("odometer")?.includes(category)}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "odometerPerYear"]}
            label={
              <LabelWithPopover
                label={t("calc.vehicle.attrs.vehicleData.odometerPerYear")}
                className="margin-left-tiny"
                popoverContent={
                  <table className="data-table-view wide-margins" style={{ maxWidth: 650 }}>
                    <tbody>
                      {odometerPerYearDescriptions.map((value) => (
                        <tr key={value}>
                          <td>
                            <b>{t("calc.vehicle.helpers.odometerPerYearDesc.institution" + value)}</b>
                          </td>
                          <td>{t("calc.vehicle.helpers.odometerPerYearDesc.value" + value)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                }
              />
            }
            dependencies={[["vehicleData", "category"]]}
            rules={[
              {
                validator: (_, value) =>
                  isDefined(value) && !resolveOdometerPerYearValues(category).includes(value)
                    ? Promise.reject(t("calc.vehicle.validations.odometerPerYearInvalid"))
                    : Promise.resolve(),
              },
            ]}
          >
            <Select
              {...selectStandardProps}
              allowClear
              disabled={!SPECS_MAP.get("odometerPerYear")?.includes(category) || odometerPerYearOptions.length === 0}
              options={odometerPerYearOptions}
            ></Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "steeringWheelOnTheRight"]}
            valuePropName="checked"
            className={classNames({ "form-item-without-label": importedChecked })}
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox
              style={importedChecked ? { maxHeight: 22 } : undefined}
              disabled={calcType === CalcType.CRASH || !SPECS_MAP.get("steeringWheelOnTheRight")?.includes(category)}
            >
              {t("calc.vehicle.attrs.vehicleData.steeringWheelOnTheRight")}
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "emergencyBrakingSystem"]}
            valuePropName="checked"
            className={classNames({ "form-item-without-label": importedChecked })}
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox
              style={importedChecked ? { maxHeight: 22 } : undefined}
              disabled={calcType === CalcType.CRASH || !SPECS_MAP.get("emergencyBrakingSystem")?.includes(category)}
            >
              <LabelWithTooltip
                label={t("calc.vehicle.attrs.vehicleData.emergencyBrakingSystem")}
                tooltip={t("calc.vehicle.helpers.emergencyBrakingSystemDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "parkingAssistant"]}
            valuePropName="checked"
            className={classNames({ "form-item-without-label": importedChecked })}
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox
              style={importedChecked ? { maxHeight: 22 } : undefined}
              disabled={calcType === CalcType.CRASH || !SPECS_MAP.get("parkingAssistant")?.includes(category)}
            >
              <LabelWithTooltip
                label={t("calc.vehicle.attrs.vehicleData.parkingAssistant")}
                tooltip={t("calc.vehicle.helpers.parkingAssistantDesc")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "garaged"]}
            valuePropName="checked"
            className={classNames({ "form-item-without-label": importedChecked })}
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={importedChecked ? { maxHeight: 22 } : undefined}>
              <LabelWithTooltip
                tooltip={t("calc.vehicle.helpers.garagedHint")}
                label={t("calc.vehicle.attrs.vehicleData.garaged")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "imported"]}
            valuePropName="checked"
            className={classNames({ "form-item-without-label": importedChecked })}
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox style={importedChecked ? { maxHeight: 22 } : undefined}>
              {t("calc.vehicle.attrs.vehicleData.imported")}
            </Checkbox>
          </Form.Item>
        </Col>

        {importedChecked && (
          <Col span={colSpan}>
            <Form.Item
              name={["vehicleData", "importCountry"]}
              label={t("calc.vehicle.enums.vehicleImportCountry._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.keys(VehicleImportCountry).map((country) => ({
                  value: country,
                  label: t("calc.vehicle.enums.vehicleImportCountry." + country),
                }))}
              />
            </Form.Item>
          </Col>
        )}
      </Row>

      <Row gutter={rowGutter}>
        <Col span={13}>
          <Divider className="divider-subheader">{t("calc.vehicle.sections.vehiclePrice")}</Divider>

          <Row gutter={rowGutter}>
            <Col span={24}>
              <div className="margin-bottom-small">
                <SecondaryButton
                  size="small"
                  icon={<AntIcon type="euro" />}
                  loading={getVehiclePrice.isPending}
                  onClick={handleVehiclePriceRequestSubmit}
                  disabled={calcType === CalcType.MTPL || !VEHICLE_CATEGORY_M1_N1.includes(category)}
                >
                  {t("calc.vehicle.actions.getVehiclePrice")}
                </SecondaryButton>
              </div>
            </Col>
          </Row>

          <Row gutter={rowGutter}>
            <Col span={threeColumnColSpan}>
              <Form.Item
                name={["vehicleData", "price"]}
                label={
                  <LabelWithTooltip
                    label={t("calc.vehicle.attrs.vehicleData.price")}
                    tooltip={t("calc.vehicle.helpers.priceDesc")}
                  />
                }
                rules={[validations.minNumber(1)]}
              >
                <InputNumber
                  addon="euro"
                  disabled={
                    calcType === CalcType.MTPL &&
                    (!newVehicle || (!VEHICLE_CATEGORY_M1_N1.includes(category) && !isMotorcycleWithReinsurance))
                  }
                />
              </Form.Item>
            </Col>

            <Col span={threeColumnColSpan}>
              <Form.Item
                name={["vehicleData", "generalPrice"]}
                label={
                  <LabelWithTooltip
                    label={t("calc.vehicle.attrs.vehicleData.generalPrice")}
                    tooltip={t("calc.vehicle.helpers.generalPriceDesc")}
                  />
                }
                rules={[
                  validations.minNumber(1),
                  validations.maxNumber(price, t("calc.vehicle.attrs.vehicleData.price")),
                ]}
              >
                <InputNumber addon="euro" disabled={calcType === CalcType.MTPL} />
              </Form.Item>
            </Col>

            <Col span={threeColumnColSpan}>
              <Form.Item
                name={["vehicleData", "buyingPrice"]}
                label={t("calc.vehicle.attrs.vehicleData.buyingPrice")}
                rules={[
                  validations.minNumber(1),
                  validations.maxNumber(price, t("calc.vehicle.attrs.vehicleData.price")),
                ]}
              >
                <InputNumber addon="euro" disabled={calcType === CalcType.MTPL} />
              </Form.Item>
            </Col>
          </Row>
        </Col>

        <Col span={11}>
          <Divider className="divider-subheader">{t("calc.vehicle.sections.vehicleSecurity")}</Divider>

          <Row gutter={rowGutter} className="margin-top-large">
            {Object.values(VehicleSecurityFeature).map((feature, index) => (
              <Col span={threeColumnColSpan} key={feature}>
                <HiddenInput name={["vehicleData", "security", index, "feature"]} initialValue={feature} />

                <Form.Item
                  name={["vehicleData", "security", index, "selected"]}
                  valuePropName="checked"
                  rules={[validations.none]}
                  initialValue={false}
                >
                  <Checkbox>{t("calc.vehicle.enums.vehicleSecurityFeature." + feature)}</Checkbox>
                </Form.Item>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>

      <HiddenInput name={["vehicleData", "registrationCertificatePresent"]} initialValue={false} />
      <HiddenInput name={["vehicleData", "registrationCertificateNumber"]} />
      <HiddenInput name={["vehicleData", "previousRegistrationCertificateNumber"]} />
      <HiddenInput name={["vehicleData", "colorId"]} />
    </Card>
  );
};
